import React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
    Stack,
    Grid,
} from '@mui/material';
import GenericModal from '../../../components/GenericModal';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
    clearFinancePaymentError,
    clearSelectedFinancePayment,
    fetchFinancePayment,
    fetchFinancePayments,
} from '../../../store/slices/financePaymentsSlice';

interface PaymentsTabProps {
    selectedCustomerId?: number;
}

const PaymentsTab: React.FC<PaymentsTabProps> = ({ selectedCustomerId }) => {
    const dispatch = useAppDispatch();
    const paymentsState = useAppSelector((state) => state.financePayments);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    React.useEffect(() => {
        if (selectedCustomerId) {
            dispatch(fetchFinancePayments({ customerId: selectedCustomerId }));
        }
    }, [dispatch, selectedCustomerId]);

    return (
        <Card sx={{ overflow: 'hidden' }}>
            <CardHeader
                title="Payment History"
                action={
                    <IconButton
                        onClick={() => dispatch(fetchFinancePayments({ customerId: selectedCustomerId }))}
                    >
                        <RefreshIcon />
                    </IconButton>
                }
            />
            <CardContent>
                {paymentsState.loadingList && <CircularProgress />}
                {paymentsState.error && (
                    <Alert severity="error" onClose={() => dispatch(clearFinancePaymentError())}>
                        {paymentsState.error}
                    </Alert>
                )}
                {isMobile ? (
                    <Stack spacing={2}>
                        {paymentsState.list.map((payment) => (
                            <Card key={payment.id} variant="outlined">
                                <CardContent>
                                    <Grid container spacing={1}>
                                        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="subtitle2">Slip #{payment.unique_id}</Typography>
                                            <Typography variant="subtitle2" fontWeight="bold">Rs. {payment.amount.toLocaleString()}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {payment.client?.name || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Divider sx={{ my: 1 }} />
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="caption" color="text.secondary">Instrument #</Typography>
                                            <Typography variant="body2">{payment.instrument_no}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="caption" color="text.secondary">Mode</Typography>
                                            <Typography variant="body2">{payment.payment_mode}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="caption" color="text.secondary">Date</Typography>
                                            <Typography variant="body2">
                                                {payment.payment_date
                                                    ? new Date(payment.payment_date).toLocaleDateString()
                                                    : '—'}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {payment.attachment && (
                                                <Button
                                                    size="small"
                                                    href={payment.attachment}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View Att.
                                                </Button>
                                            )}
                                        </Grid>
                                        <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                fullWidth
                                                onClick={() => dispatch(fetchFinancePayment(payment.id))}
                                            >
                                                View Details
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 800 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Slip #</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell>Instrument #</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Mode</TableCell>
                                    <TableCell>Attachment</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paymentsState.list.map((payment) => (
                                    <TableRow key={payment.id}>
                                        <TableCell>{payment.unique_id}</TableCell>
                                        <TableCell>{payment.client?.name || 'N/A'}</TableCell>
                                        <TableCell align="right">
                                            Rs. {payment.amount.toLocaleString()}
                                        </TableCell>
                                        <TableCell>{payment.instrument_no}</TableCell>
                                        <TableCell>
                                            {payment.payment_date
                                                ? new Date(payment.payment_date).toLocaleDateString()
                                                : '—'}
                                        </TableCell>
                                        <TableCell>{payment.payment_mode}</TableCell>
                                        <TableCell>
                                            {payment.attachment ? (
                                                <Button
                                                    size="small"
                                                    href={payment.attachment}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View
                                                </Button>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => dispatch(fetchFinancePayment(payment.id))}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
                {/* Payment Detail Dialog */}
                <GenericModal
                    open={Boolean(paymentsState.selectedPayment)}
                    onClose={() => dispatch(clearSelectedFinancePayment())}
                    title="Payment Detail"
                    maxWidth="md"
                    fullWidth
                    actions={
                        <Button onClick={() => dispatch(clearSelectedFinancePayment())}>Close</Button>
                    }
                >
                    {paymentsState.loadingDetail && <CircularProgress />}
                    {paymentsState.selectedPayment && (
                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Slip #{paymentsState.selectedPayment.unique_id}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Instrument #{paymentsState.selectedPayment.instrument_no} –{' '}
                                {paymentsState.selectedPayment.payment_mode}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Date:{' '}
                                {new Date(
                                    paymentsState.selectedPayment.payment_date
                                ).toLocaleDateString()}
                            </Typography>

                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" gutterBottom>
                                Consignments
                            </Typography>
                            {isMobile ? (
                                <Stack spacing={2} sx={{ mb: 2 }}>
                                    {paymentsState.selectedPayment.consignments.map((item) => (
                                        <Card key={item.id} variant="outlined">
                                            <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="subtitle2">{item.consignment_no}</Typography>
                                                    <Typography variant="subtitle2" fontWeight="bold">Rs. {item.paid_amount.toLocaleString()}</Typography>
                                                </Box>
                                                <Divider sx={{ mb: 1 }} />
                                                <Grid container spacing={1}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">COD</Typography>
                                                        <Typography variant="body2">Rs. {item.cod_amount.toLocaleString()}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">Service Charges</Typography>
                                                        <Typography variant="body2">Rs. {item.service_charges.toLocaleString()}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">Tax</Typography>
                                                        <Typography variant="body2">Rs. {item.tax.toLocaleString()}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            ) : (
                                <Box sx={{ overflowX: 'auto', mb: 2 }}>
                                    <Table size="small" sx={{ minWidth: 600 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Consignment</TableCell>
                                                <TableCell align="right">COD</TableCell>
                                                <TableCell align="right">Service Charges</TableCell>
                                                <TableCell align="right">Tax</TableCell>
                                                <TableCell align="right">Paid Amount</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paymentsState.selectedPayment.consignments.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell>{item.consignment_no}</TableCell>
                                                    <TableCell align="right">
                                                        Rs. {item.cod_amount.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Rs. {item.service_charges.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Rs. {item.tax.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Rs. {item.paid_amount.toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}

                            <Typography variant="subtitle2" gutterBottom>
                                Debit / Credit Notes
                            </Typography>
                            {isMobile ? (
                                <Stack spacing={2}>
                                    {paymentsState.selectedPayment.notes.map((note) => (
                                        <Card key={note.id} variant="outlined">
                                            <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="subtitle2">Note #{note.unique_id}</Typography>
                                                    <Typography variant="subtitle2" fontWeight="bold">Rs. {note.amount.toLocaleString()}</Typography>
                                                </Box>
                                                <Divider sx={{ mb: 1 }} />
                                                <Grid container spacing={1}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">Type</Typography>
                                                        <Typography variant="body2">{note.type}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">Tax</Typography>
                                                        <Typography variant="body2">Rs. {note.tax.toLocaleString()}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            ) : (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Table size="small" sx={{ minWidth: 500 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Note #</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell align="right">Amount</TableCell>
                                                <TableCell align="right">Tax</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paymentsState.selectedPayment.notes.map((note) => (
                                                <TableRow key={note.id}>
                                                    <TableCell>{note.unique_id}</TableCell>
                                                    <TableCell>{note.type}</TableCell>
                                                    <TableCell align="right">
                                                        Rs. {note.amount.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Rs. {note.tax.toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </Box>
                    )
                    }
                </GenericModal >
            </CardContent >
        </Card >
    );
};

export default PaymentsTab;
